'use client'

import { useEffect, useState } from 'react'

/**
 * "Gallery · Field" mosaic hero cell — live currency rates against the
 * country's base currency (open.er-api.com, no key, CORS-friendly).
 * Falls back to indicative values offline.
 */
export default function CountryRatesHeroCell({ base = 'EUR', symbol = '€', lang = 'ru' }) {
    const isRu = lang === 'ru'
    const code = /^[A-Za-z]{3}$/.test(base) ? base.toUpperCase() : 'EUR'

    // Локализованное название валюты по ISO-коду (перенесено из отдельного факта
    // «Валюта»). Для неизвестного кода Intl вернёт сам код — тогда имя не показываем.
    let currencyName = ''
    try {
        const dn = new Intl.DisplayNames([isRu ? 'ru' : 'en'], { type: 'currency' }).of(code)
        if (dn && dn.toUpperCase() !== code.toUpperCase()) {
            currencyName = dn.charAt(0).toUpperCase() + dn.slice(1)
        }
    } catch {
        /* нет ICU-данных — покажем без названия */
    }

    const [rates, setRates] = useState(null)
    const [when, setWhen] = useState(isRu ? 'обновляется…' : 'updating…')

    useEffect(() => {
        let alive = true
        const targets = ['USD', 'RUB', 'GBP'].filter((c) => c !== code)
        fetch(`https://open.er-api.com/v6/latest/${code}`)
            .then((r) => r.json())
            .then((d) => {
                if (!alive || !d?.rates) throw new Error('no rates')
                const fmt = { USD: (v) => '$' + v.toFixed(2), RUB: (v) => '₽' + Math.round(v), GBP: (v) => '£' + v.toFixed(2) }
                setRates(targets.map((c) => ({ d: c, t: d.rates[c] != null ? fmt[c](d.rates[c]) : '—' })))
                const dt = d.time_last_update_utc ? new Date(d.time_last_update_utc) : new Date()
                setWhen((isRu ? 'обновлено ' : 'updated ') + new Intl.DateTimeFormat(isRu ? 'ru-RU' : 'en-US', { day: '2-digit', month: 'short' }).format(dt))
            })
            .catch(() => {
                if (!alive) return
                setRates(targets.map((c) => ({ d: c, t: '—' })))
                setWhen(isRu ? 'ориентировочно' : 'indicative')
            })
        return () => { alive = false }
    }, [code, isRu])

    return (
        <div className="cell hero">
            <span className="k">{isRu ? `Валюта · курс 1 ${code}` : `Currency · rate 1 ${code}`}</span>
            {currencyName && <span className="cur-name">{currencyName}</span>}
            <span className="big">1 {symbol}</span>
            <div className="rates">
                {(rates || [{ d: 'USD', t: '—' }, { d: 'RUB', t: '—' }, { d: 'GBP', t: '—' }]).map((r) => (
                    <div className="rc" key={r.d}>
                        <span className="d">{r.d}</span>
                        <span className="t">{r.t}</span>
                    </div>
                ))}
            </div>
            <span className="sub">{when}</span>
        </div>
    )
}
